import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Mail, Plus, Send, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FREQ_LABELS = { weekly: "Settimanale (ogni lunedì)", monthly: "Mensile (1° del mese)" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

/**
 * Sprint 10.A — Scheduled Reports section for Profile.
 * Gated on `advanced_reports` (Pro + Trial). Free users see an upgrade CTA.
 */
export default function ScheduledReportsSection({ businesses }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [maxExtra, setMaxExtra] = useState(3);
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduled-reports");
      setItems(data.items || []);
      setMaxExtra(data.max_extra_recipients || 3);
      setGated(false);
    } catch (e) {
      if (e?.response?.status === 402) setGated(true);
      else toast.error("Impossibile caricare i report programmati");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const del = async (id) => {
    if (!window.confirm("Eliminare questo report programmato?")) return;
    setBusyId(id);
    try {
      await api.delete(`/scheduled-reports/${id}`);
      setItems((prev) => prev.filter((x) => x.schedule_id !== id));
      toast.success("Report eliminato");
    } catch { toast.error("Errore durante l'eliminazione"); }
    finally { setBusyId(null); }
  };

  const toggle = async (item) => {
    setBusyId(item.schedule_id);
    try {
      const { data } = await api.patch(`/scheduled-reports/${item.schedule_id}`, {
        enabled: !item.enabled,
      });
      setItems((prev) => prev.map((x) => x.schedule_id === item.schedule_id ? data : x));
    } catch { toast.error("Impossibile aggiornare lo stato"); }
    finally { setBusyId(null); }
  };

  const sendNow = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/scheduled-reports/${id}/send-now`);
      if (data.status === "sent") toast.success(`Email inviata (${data.reviews} recensioni)`);
      else if (data.status === "skipped_no_email") toast.warning("Email non configurata sul server");
      else toast.error(data.error || "Invio fallito");
      reload();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error("Serve un piano Pro attivo");
      else toast.error("Invio fallito");
    } finally { setBusyId(null); }
  };

  return (
    <motion.section
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      data-testid="scheduled-reports-section"
      className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
    >
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
            Automazione — Pro
          </div>
          <h2 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight flex items-center gap-2">
            <CalendarClock size={20} className="text-[#00F2F4]" /> Report automatici via email
          </h2>
          <p className="mt-2 text-sm text-[var(--fg-3)] max-w-lg">
            Ricevi (e condividi con il tuo team) il report PDF settimanale o mensile senza dover accedere alla dashboard.
          </p>
        </div>
        {!gated && (
          <Button
            data-testid="scheduled-reports-add-btn"
            onClick={() => setAddOpen(true)}
            disabled={!businesses?.length}
            className="rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] gap-2"
          >
            <Plus size={14} /> Nuovo
          </Button>
        )}
      </div>

      {gated ? (
        <div className="rounded-xl border border-[#00F2F4]/40 bg-[#00F2F4]/8 p-5 text-sm text-[var(--fg-2)]"
             data-testid="scheduled-reports-upgrade">
          <div className="font-sans font-semibold text-[var(--fg-1)]">Disponibile su RecenUp Pro</div>
          <p className="mt-1">Sblocca i report automatici settimanali/mensili con Pro o il trial di 14 giorni.</p>
          <a href="/pricing" className="mt-3 inline-block text-[#00A8AA] hover:text-[#00F2F4] font-sans text-sm">
            Scopri Pro →
          </a>
        </div>
      ) : loading ? (
        <div className="py-8 text-center text-[var(--fg-3)] text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border-c)] p-8 text-center text-sm text-[var(--fg-3)]"
             data-testid="scheduled-reports-empty">
          Nessun report programmato. Crea il primo per ricevere le tue recensioni via email.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const biz = businesses?.find((b) => b.business_id === it.business_id);
            return (
              <div
                key={it.schedule_id}
                data-testid={`scheduled-report-${it.schedule_id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border-c)] bg-[var(--bg-1)] p-4"
              >
                <div className="min-w-0">
                  <div className="font-sans text-sm font-semibold text-[var(--fg-1)]">
                    {biz?.name || it.business_id}
                  </div>
                  <div className="mt-0.5 text-xs text-[var(--fg-3)]">
                    {FREQ_LABELS[it.frequency]} · Prossimo invio: {fmtDate(it.next_run_at)}
                  </div>
                  {it.extra_recipients?.length > 0 && (
                    <div className="mt-1 text-[11px] text-[var(--fg-3)] flex items-center gap-1">
                      <Mail size={11} /> +{it.extra_recipients.length} destinatari extra
                    </div>
                  )}
                  {it.last_run_status === "error" && (
                    <div className="mt-1 text-[11px] text-[#C6423B]">Ultimo invio: errore</div>
                  )}
                  {it.last_run_status === "sent" && it.last_run_at && (
                    <div className="mt-1 text-[11px] text-[#4A8A6E]">
                      Ultimo invio: {fmtDate(it.last_run_at)} · {it.last_reviews_count ?? 0} recensioni
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--fg-3)]">
                      {it.enabled ? "Attivo" : "In pausa"}
                    </span>
                    <Switch
                      checked={!!it.enabled}
                      onCheckedChange={() => toggle(it)}
                      disabled={busyId === it.schedule_id}
                      data-testid={`scheduled-report-toggle-${it.schedule_id}`}
                    />
                  </div>
                  <button
                    onClick={() => sendNow(it.schedule_id)}
                    disabled={busyId === it.schedule_id}
                    data-testid={`scheduled-report-sendnow-${it.schedule_id}`}
                    title="Invia ora"
                    className="p-2 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] hover:text-[#00F2F4] text-[var(--fg-2)]"
                    style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
                  >
                    <Send size={14} />
                  </button>
                  <button
                    onClick={() => del(it.schedule_id)}
                    disabled={busyId === it.schedule_id}
                    data-testid={`scheduled-report-delete-${it.schedule_id}`}
                    title="Elimina"
                    className="p-2 rounded-full border border-[var(--border-c)] hover:border-[#C6423B] hover:text-[#C6423B] text-[var(--fg-2)]"
                    style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddScheduleDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        businesses={businesses || []}
        maxExtra={maxExtra}
        onCreated={reload}
      />
    </motion.section>
  );
}

function AddScheduleDialog({ open, onOpenChange, businesses, maxExtra, onCreated }) {
  const [businessId, setBusinessId] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [extras, setExtras] = useState([""]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setBusinessId(businesses[0]?.business_id || "");
      setFrequency("weekly");
      setExtras([""]);
    }
  }, [open, businesses]);

  const addExtra = () => {
    if (extras.length >= maxExtra) return;
    setExtras([...extras, ""]);
  };
  const removeExtra = (i) => setExtras(extras.filter((_, idx) => idx !== i));
  const setExtra = (i, v) => setExtras(extras.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    if (!businessId) { toast.error("Seleziona un'attività"); return; }
    const cleaned = extras.map((e) => e.trim()).filter(Boolean);
    setSaving(true);
    try {
      await api.post("/scheduled-reports", {
        business_id: businessId,
        frequency,
        extra_recipients: cleaned,
      });
      toast.success("Report programmato creato");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) toast.error(detail?.message || "Esiste già un report con questa frequenza");
      else if (status === 402) toast.error("Serve un piano Pro attivo");
      else if (typeof detail === "string" && detail.startsWith("invalid_email"))
        toast.error("Email non valida: " + detail.split(":")[1]);
      else if (detail === "too_many_recipients") toast.error(`Massimo ${maxExtra} email extra`);
      else toast.error("Impossibile creare il report");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="scheduled-report-dialog"
        className="bg-[var(--bg-1)] border-[var(--border-c)] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[var(--fg-1)]">
            Nuovo report programmato
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
              Attività
            </Label>
            <Select value={businessId} onValueChange={setBusinessId}>
              <SelectTrigger data-testid="scheduled-report-business-select" className="mt-2">
                <SelectValue placeholder="Seleziona attività" />
              </SelectTrigger>
              <SelectContent>
                {businesses.map((b) => (
                  <SelectItem key={b.business_id} value={b.business_id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
              Frequenza
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {Object.entries(FREQ_LABELS).map(([id, lbl]) => (
                <button
                  key={id}
                  data-testid={`scheduled-report-freq-${id}`}
                  onClick={() => setFrequency(id)}
                  className={`p-3 rounded-xl border text-left text-sm font-sans ${
                    frequency === id
                      ? "border-[#00F2F4] bg-[#00F2F4]/10 text-[#00F2F4]"
                      : "border-[var(--border-c)] text-[var(--fg-2)] hover:border-[var(--fg-3)]"
                  }`}
                  style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
              Destinatari extra (opzionale, max {maxExtra})
            </Label>
            <div className="mt-2 space-y-2">
              {extras.map((e, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    data-testid={`scheduled-report-extra-${i}`}
                    type="email"
                    value={e}
                    placeholder="team@esempio.it"
                    onChange={(ev) => setExtra(i, ev.target.value)}
                  />
                  {extras.length > 1 && (
                    <button
                      onClick={() => removeExtra(i)}
                      className="p-2 rounded-full text-[var(--fg-3)] hover:text-[#C6423B]"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {extras.length < maxExtra && (
                <button
                  onClick={addExtra}
                  data-testid="scheduled-report-add-extra"
                  className="text-xs text-[#00A8AA] hover:text-[#00F2F4] font-sans"
                >
                  + Aggiungi destinatario
                </button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
          >
            Annulla
          </Button>
          <Button
            data-testid="scheduled-report-save-btn"
            onClick={submit}
            disabled={saving}
            className="rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A]"
          >
            {saving ? "Salvataggio…" : "Crea report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
